import React, { useEffect } from 'react';
import List from '@mui/material/List';
import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import ListItemIcon from '@mui/material/ListItemIcon';
import Checkbox from '@mui/material/Checkbox';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Box from '@mui/material/Box';

function not(a, b) {
  return a.filter((aItem) => !b.some((bItem) => bItem.id === aItem.id));
}

function intersection(a, b) {
  return a.filter((aItem) => b.some((bItem) => bItem.id === aItem.id));
}

function union(a, b) {
  return [...a, ...not(b, a)];
}

const ServiceCentersTransferList = ({
  availableCenters = [],
  selectedCenters = [],
  onSelectionChange,
}) => {
  const [checked, setChecked] = React.useState([]);
  const [left, setLeft] = React.useState([]);
  const [right, setRight] = React.useState([]);

  useEffect(() => {
    const filteredLeft = not(availableCenters, selectedCenters);
    const filteredRight = intersection(availableCenters, selectedCenters);

    setLeft(filteredLeft);
    setRight(filteredRight);
  }, [availableCenters, selectedCenters]);

  const leftChecked = intersection(checked, left);
  const rightChecked = intersection(checked, right);

  const handleToggle = (value) => () => {
    const currentIndex = checked.findIndex((item) => item.id === value.id);
    const newChecked = [...checked];

    if (currentIndex === -1) {
      newChecked.push(value);
    } else {
      newChecked.splice(currentIndex, 1);
    }

    setChecked(newChecked);
  };

  const numberOfChecked = (items) => intersection(checked, items).length;

  const handleToggleAll = (items) => () => {
    if (numberOfChecked(items) === items.length) {
      setChecked(not(checked, items));
    } else {
      setChecked(union(checked, items));
    }
  };

  const handleCheckedRight = () => {
    const filteredLeftChecked = not(leftChecked, right);
    const newLeft = not(left, filteredLeftChecked);
    const newRight = [...right, ...filteredLeftChecked];
    const newChecked = not(checked, leftChecked);

    setRight(newRight);
    setLeft(newLeft);
    setChecked(newChecked);
    onSelectionChange(newRight);
  };

  const handleCheckedLeft = () => {
    const filteredRightChecked = not(rightChecked, left);
    const newLeft = [...left, ...filteredRightChecked];
    const newRight = not(right, filteredRightChecked);
    const newChecked = not(checked, rightChecked);

    setLeft(newLeft);
    setRight(newRight);
    setChecked(newChecked);
    onSelectionChange(newRight);
  };

  const customList = (title, subtitle, items) => (
    <Card sx={{ minWidth: '100%' }}>
      <CardHeader
        sx={{ px: 2, py: 1 }}
        avatar={
          <Checkbox
            onClick={handleToggleAll(items)}
            checked={numberOfChecked(items) === items.length && items.length !== 0}
            indeterminate={
              numberOfChecked(items) !== items.length && numberOfChecked(items) !== 0
            }
            disabled={items.length === 0}
            inputProps={{
              'aria-label': 'all items selected',
            }}
          />
        }
        title={title}
        subheader={subtitle}
      />
      <Divider />
      <List
        sx={{
          width: '100%',
          height: 230,
          bgcolor: 'background.paper',
          overflow: 'auto',
        }}
        dense
        component="div"
        role="list"
      >
        {items.map((value) => {
          const labelId = `transfer-list-all-item-${value.id}-label`;

          return (
            <ListItemButton
              key={value.id}
              role="listitem"
              onClick={handleToggle(value)}
            >
              <ListItemIcon>
                <Checkbox
                  checked={checked.some((item) => item.id === value.id)}
                  tabIndex={-1}
                  disableRipple
                  inputProps={{
                    'aria-labelledby': labelId,
                  }}
                />
              </ListItemIcon>
              <ListItemText id={labelId} primary={value.des_centro_servico} />
            </ListItemButton>
          );
        })}
      </List>
    </Card>
  );

  return (
    <Box
      sx={{
        display: 'flex',
        gap: 2,
        alignItems: 'center',
        width: '100%',
        flexWrap: 'nowrap',
        justifyContent: 'space-between'
      }}
    >
      <Box sx={{ flex: 1 }}>
        {customList(
          'Centros Disponíveis',
          `${left.length} disponíve${left.length < 2 ? 'l' : 'is'}`,
          left,
        )}
      </Box>
      <Box sx={{ width: 64 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Button
            sx={{ my: 0.5 }}
            variant="outlined"
            size="small"
            onClick={handleCheckedRight}
            disabled={leftChecked.length === 0}
            aria-label="move selected right"
          >
            &gt;
          </Button>
          <Button
            sx={{ my: 0.5 }}
            variant="outlined"
            size="small"
            onClick={handleCheckedLeft}
            disabled={rightChecked.length === 0}
            aria-label="move selected left"
          >
            &lt;
          </Button>
        </Box>
      </Box>
      <Box sx={{ flex: 1 }}>
        {customList(
          'Centros Selecionados',
          `${right.length} selecionado${right.length < 2 ? '' : 's'}`,
          right
        )}
      </Box>
    </Box>
  );
};

export default ServiceCentersTransferList;
